import {
  ChangeEvent,
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { Card, Col, Container, Row } from "reactstrap";
import { Btn, H2 } from "../../../../AbstractElements";
import { useAppDispatch, useAppSelector } from "../../../../ReduxToolkit/Hooks";
import {
  closeModal,
  openModal,
} from "../../../../ReduxToolkit/Slice/UserProfileDetailsModalSlice";
import useUser from "../../../../hooks/useUser";
import { ModalType } from "../../../../Types/UserProfileDetails";
import {
  approvalLeaveModal,
  bulkApprovedLeaveTeamRequestList,
} from "../../../../utils/Constant";
import Loader from "../../../../Layout/Loader/Loader";
import FinancialYearCycle from "../../../../CommonElements/YearPickerCycle";
import { setSelectedFinancialYear } from "../../../../ReduxToolkit/Slice/FinancialYearSlice";
import { Nullable } from "../../../../Types/Common";
import BulkActionBar from "../../../../CommonElements/BulkActionBar";
import { useIsLoading } from "../../../../hooks/useIsLoading";
import { teamMembersLeaveApprovedLoader } from "../redux/loader/teamMembersLeaveApprovedLoader";
import { useModalSelector } from "../../../../hooks/useModalSelector";
import TeamMembersLeaveRequestTable from "./table/TeamMembersLeaveRequestTable";
import TeamMembersLeaveRequestCard from "./cards/TeamMembersLeaveRequestCard";
import {
  CheckboxLeavesRequest,
  ILeaveData,
  SelectedRow,
  TeamMembersLeaveStatusFilter,
} from "../types";
import {
  useTeamMembersBulkApproveLeaveMutation,
  useTeamMembersLeaveListQuery,
} from "../../../../ReduxToolkit/api/teamMembers";
import { skipToken } from "@reduxjs/toolkit/query";
import { logErrors } from "../../../../utils/logErrors";
import { showApiResultAlert } from "../../../../utils/AlertManager/showApiAlert";
import { useEmployeeProfile } from "../../../../hooks/useEmployeeProfile";
import { useLeaveTypesListQuery } from "../../../../ReduxToolkit/api/leaves";
import { fetchEmployeesListThunk } from "../../../hrZone/Users/redux/slice/EmployeesListSlie";
import { selectEmployeesList } from "../../../../ReduxToolkit/Selectors/EmployeeListSelector";
import { fetchAssginedManagerToEmployee } from "../../../hrZone/Users/redux/slice/AssignedManagerToEmployeeSlice";
import { leaveApplyModal } from "../../../../utils/Constant";

const EmployeeLeaveFormModal = lazy(
  () => import("../../../leave/components/modal/EmployeeLeaveFormModal"),
);

const leaveStatusFilterOptions: Array<{
  label: string;
  value: TeamMembersLeaveStatusFilter;
}> = [
  { label: "Pending", value: "pending" },
  { label: "Approved", value: "approved" },
  { label: "Rejected", value: "rejected" },
];

const FULL_ACCESS_ROLES = new Set(["admin", "manager", "hr"]);

const TeamMembersLeaveBulkApprovedModal = lazy(
  () => import("./modal/TeamMembersLeaveBulkApprovedModal"),
);

const TeamMembersLeaveApprovedModal = lazy(
  () => import("./modal/TeamMembersLeaveApprovedModal"),
);

const TeamMembersLeave = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const user = useUser();
  const usersResponse = useAppSelector(selectEmployeesList);
  const [adjustmentType, setAdjustmentType] = useState<"credit" | "debit">("debit");
  const [adjustmentUserId, setAdjustmentUserId] = useState<number>();
  const [adjustmentManagerReady, setAdjustmentManagerReady] = useState(false);
  const adjustmentUserRef = useRef<number>();

  const userType = String(user?.user_type ?? "")
    .toLowerCase()
    .replaceAll(" ", "");

  const [filterText, setFilterText] = useState<string>("");
  const [leaveStatusFilter, setLeaveStatusFilter] =
    useState<TeamMembersLeaveStatusFilter>("pending");

  const [searchEmployeeModal, setSearchEmployeeModal] =
    useState<Nullable<string>>(null);

  const [toggleCleared, setToggleCleared] = useState<boolean>(false);
  const [selectRows, setSelectedRows] = useState<Nullable<SelectedRow>>(null);

  const [allTeamLeaveRequests, setAllTeamLeaveRequest] = useState<
    CheckboxLeavesRequest[]
  >([]);

  const [checkboxTeamLeaveList, setCheckboxTeamLeavesRequest] = useState<
    CheckboxLeavesRequest[]
  >([]);

  const [selectAllLeaveRequest, setSelectAllLeaveRequest] =
    useState<boolean>(false);

  const [selectedEmployeeInfo, setSelectedEmployeeInfo] =
    useState<Nullable<ILeaveData>>(null);

  const { isOpen, modalType, selectedId } = useModalSelector();
  const { employeeProfileDetails, loggedInEmployee } = useEmployeeProfile();

  const selectedFinancialYear = useAppSelector(
    ({ financialYearSlice }) =>
      financialYearSlice.teamMemberLeave.selectedFinancialYear,
  );

  const isLoading = useIsLoading(teamMembersLeaveApprovedLoader);

  const financialYear = selectedFinancialYear?.split("-")[0];
  const employeeId = employeeProfileDetails?.jobInformation?.userId;

  const isHrPage = location.pathname.includes("/hr-zone/employee-leaves");
  const canCreateAdjustment = isHrPage && ["hr", "admin"].includes(userType);
  const isEmployeeProfilePath = location.pathname.includes(
    "/user-management/user",
  );

  // Admin / Manager / HR visiting the HR employee-leaves page get all actions.
  const hasFullLeaveAccess = isHrPage && FULL_ACCESS_ROLES.has(userType) || FULL_ACCESS_ROLES.has(userType);

  const title = isHrPage
    ? "Employee's Leave Requests"
    : "Team Members Leave Requests";

  const shouldFetch =
    (user?.id && financialYear) || (employeeId && financialYear);

  const {
    data: teamLeavesData,
    isLoading: isTeamLeavesListLoading,
    isError: isTeamLeavesError,
    error: teamLeavesError,
    isFetching: isTeamLeaveListFetching,
  } = useTeamMembersLeaveListQuery(
    shouldFetch
      ? {
          user: isEmployeeProfilePath ? String(employeeId) : String(user?.id),
          activeYear: Number(financialYear),
          ...(isHrPage ? { type: "hr" } : {}),
          status: leaveStatusFilter,
        }
      : skipToken,
  );

  const [
    updateTeamMembersBulkLeaveCorrection,
    {
      isLoading: isBulkApproveLeaveLoading,
      reset: bulkApproveLeaveCorrectionReset,
    },
  ] = useTeamMembersBulkApproveLeaveMutation();

  const {
    data: employeeLeaveTypeList,
  } = useLeaveTypesListQuery(
    modalType === approvalLeaveModal &&
      isOpen &&
      selectedEmployeeInfo?.team_member?.id
      ? {
          activeYear: financialYear,
          userId: selectedEmployeeInfo.team_member.id,
        }
      : skipToken,
  );

  const { data: adjustmentLeaveTypes } = useLeaveTypesListQuery(
    canCreateAdjustment && isOpen && modalType === leaveApplyModal && adjustmentUserId && financialYear
      ? { activeYear: financialYear, userId: adjustmentUserId }
      : skipToken,
  );

  const openAdjustmentModal = () => {
    setAdjustmentType("debit");
    setAdjustmentUserId(undefined);
    adjustmentUserRef.current = undefined;
    setAdjustmentManagerReady(false);
    dispatch(fetchEmployeesListThunk({ manager: true, status: 1 }));
    dispatch(openModal({ modalType: leaveApplyModal }));
  };

  const changeAdjustmentUser = async (id: number) => {
    adjustmentUserRef.current = id;
    setAdjustmentUserId(id);
    setAdjustmentManagerReady(false);
    const result = await dispatch(fetchAssginedManagerToEmployee({ id }));
    if (adjustmentUserRef.current === id) {
      setAdjustmentManagerReady(fetchAssginedManagerToEmployee.fulfilled.match(result));
    }
  };

  // On the HR employee-leaves route, all bulk actions are available to
  // admin/manager/hr for pending, approved and rejected records.
  const canBulkApprove =
    hasFullLeaveAccess &&
    ["pending", "approved", "rejected"].includes(leaveStatusFilter);

  const isTeamLeaveRequestListLoading =
    isTeamLeavesListLoading ||
    isTeamLeaveListFetching ||
    isBulkApproveLeaveLoading;

  const handleOpenModal = (
    modalKey: ModalType,
    employeeInfo: ILeaveData,
    selectedId: string | number | null,
  ) => {
    if (isHrPage && !hasFullLeaveAccess) {
      return;
    }

    dispatch(openModal({ modalType: modalKey, selectedId }));
    setSelectedEmployeeInfo(employeeInfo);
  };

  const handleRowSelected = (state: any) => {
    setSelectedRows(state);
  };

  const handleCloseModal = () => {
    setSelectedEmployeeInfo(null);
    setAdjustmentUserId(undefined);
    adjustmentUserRef.current = undefined;
    setAdjustmentManagerReady(false);
    dispatch(closeModal());
  };

  const handleSetSelectedYear = (year: string) => {
    dispatch(setSelectedFinancialYear({ module: "teamMemberLeave", year }));
  };

  const handleStatusFilterChange = (status: TeamMembersLeaveStatusFilter) => {
    setLeaveStatusFilter(status);
    setFilterText("");
    setSelectedRows(null);
    setToggleCleared((current) => !current);
  };

  const statusFilterControl = (
    <div className="d-flex align-items-center gap-2">
      <label className="f-12 fw-semibold mb-0" htmlFor="leave-status-filter">
        Status
      </label>
      <select
        id="leave-status-filter"
        className="form-select form-select-sm f-12"
        style={{ minWidth: 140 }}
        value={leaveStatusFilter}
        onChange={(event) =>
          handleStatusFilterChange(
            event.target.value as TeamMembersLeaveStatusFilter,
          )
        }
      >
        {leaveStatusFilterOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );

  const sortEmployeeLeavesRequestList = (
    a: CheckboxLeavesRequest,
    b: CheckboxLeavesRequest,
  ) => {
    if (a.isChecked !== b.isChecked) return a.isChecked ? -1 : 1;

    return a?.team_member?.name.localeCompare(b.team_member?.name);
  };

  const matchedLeave = useMemo(() => {
    try {
      if (!teamLeavesData || !Array.isArray(teamLeavesData.data)) {
        logErrors("Team leave list is empty or invalid");
        return;
      }

      if (
        selectedId === undefined ||
        selectedId === null ||
        isNaN(Number(selectedId))
      ) {
        logErrors("Selected ID is invalid.");
        return null;
      }

      const found = teamLeavesData.data.find((l) => {
        const leaveRequestId = Number(l.leave_id);

        if (isNaN(leaveRequestId)) {
          logErrors("Invalid leave request id:", l.leave_id);
          return false;
        }

        return leaveRequestId === Number(selectedId);
      });

      if (!found) {
        logErrors(`No leave found for ID: ${selectedId}`);
      }

      return found;
    } catch (error: any) {
      logErrors("Error finding matched leave:", error.message);
      return;
    }
  }, [teamLeavesData, selectedId]);

  const handleReset = () => {
    bulkApproveLeaveCorrectionReset();
    setSearchEmployeeModal(null);
    setCheckboxTeamLeavesRequest([]);
    setAllTeamLeaveRequest([]);
    setFilterText("");
    setSelectedRows(null);
    setSelectAllLeaveRequest(false);
  };

  const handleBulkLeaveRequestApproved = async (
    action: "approved" | "rejected",
  ) => {
    if (!hasFullLeaveAccess) return;

    try {
      const leaveRequestIds =
        (allTeamLeaveRequests.length > 0 &&
          allTeamLeaveRequests
            .filter((d) => d.isChecked)
            .map((d) => d.leave_id)) ||
        (selectRows?.selectedRows?.length
          ? selectRows.selectedRows.map((d) => d.leave_id)
          : []);

      if (!leaveRequestIds.length) {
        logErrors("No leaves request IDs selected for bulk action.");
        return;
      }

      const payload = {
        leave_id: leaveRequestIds,
        action,
        comment: action === "approved" ? "leave approved" : "leave rejected",
      };

      const response = await updateTeamMembersBulkLeaveCorrection(payload);

      showApiResultAlert(
        response,
        { title: "Bulk Leave Correction Info", source: "approvalLeaveModal" },
        handleReset,
      );
    } catch (error: any) {
      logErrors("Bulk leave approval failed:", error);
    }
  };

  const handleOpenTeamLeaveRequestModal = () => {
    if (!hasFullLeaveAccess) return;

    try {
      if (
        !teamLeavesData ||
        !Array.isArray(teamLeavesData.data) ||
        teamLeavesData.data.length === 0
      ) {
        logErrors("No team leave data available to display.");
        return;
      }

      const employeesLeavesRequestList = teamLeavesData.data.map(
        (d: ILeaveData) => ({
          ...d,
          isChecked: false,
        }),
      );

      setAllTeamLeaveRequest(employeesLeavesRequestList);
      setCheckboxTeamLeavesRequest(employeesLeavesRequestList);
      dispatch(openModal({ modalType: bulkApprovedLeaveTeamRequestList }));
    } catch (error: any) {
      logErrors("Error opening team leave request modal:", error);
    }
  };

  const handleCheckboxLeavesRequest = (
    e: ChangeEvent<HTMLInputElement>,
    item: CheckboxLeavesRequest,
  ) => {
    const isChecked = e.target.checked;

    setAllTeamLeaveRequest((current) =>
      current.map((req) =>
        req.leave_id === item.leave_id ? { ...req, isChecked } : req,
      ),
    );

    setCheckboxTeamLeavesRequest((current) =>
      current.map((req) =>
        req.leave_id === item.leave_id ? { ...req, isChecked } : req,
      ),
    );
  };

  const handleSelectAllLeavesRequest = (e: ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;

    const updatedAll = allTeamLeaveRequests.map((item) => {
      const matchesSearch =
        !searchEmployeeModal ||
        item.team_member.name
          .toLowerCase()
          .replaceAll(" ", "")
          .includes(searchEmployeeModal.toLowerCase().replaceAll(" ", ""));

      return matchesSearch ? { ...item, isChecked } : item;
    });

    setAllTeamLeaveRequest(updatedAll);

    const filteredList = updatedAll.filter(
      (item) =>
        !searchEmployeeModal ||
        item.team_member.name
          .toLowerCase()
          .replaceAll(" ", "")
          .includes(searchEmployeeModal.toLowerCase().replaceAll(" ", "")),
    );

    setCheckboxTeamLeavesRequest(filteredList);
    setSelectAllLeaveRequest(isChecked);
  };

  const handleSearchEmployeeLeaveRequest = (
    e: ChangeEvent<HTMLInputElement>,
  ) => {
    const inputValue = e.target.value.trim();

    if (!inputValue) {
      setCheckboxTeamLeavesRequest(allTeamLeaveRequests);
      setSelectAllLeaveRequest(false);
    } else {
      setCheckboxTeamLeavesRequest(
        allTeamLeaveRequests.filter((item) =>
          item.team_member?.name
            ?.toLowerCase()
            .includes(inputValue.toLowerCase()),
        ),
      );
    }

    setSearchEmployeeModal(inputValue);
  };

  useEffect(() => {
    if (checkboxTeamLeaveList.length) {
      const selectedRowData = {
        allSelected: true,
        selectedCount:
          checkboxTeamLeaveList.filter((d: any) => d.isChecked).length || 0,
        selectedRows:
          checkboxTeamLeaveList
            .filter((d: any) => d.isChecked)
            .map((d: any) => ({
              against_compoff_date: d.against_compoff_date ?? null,
              end_date: d.end_date,
              leave_id: d.leave_id,
              leave_type: d.leave_type,
              reason: d.reason,
              status: d.status,
              start_date: d.start_date,
              total_days: String(d.total_days),
              team_member: {
                id: d.team_member.id,
                name: d.team_member.name,
                email: d.team_member.email,
                status: d.team_member.status,
              },
            })) || [],
      };

      setSelectedRows(selectedRowData);
    }
  }, [checkboxTeamLeaveList]);

  useEffect(() => {
    if (isTeamLeavesError) {
      showApiResultAlert(teamLeavesError, {
        title: `Hey ${loggedInEmployee?.name}!`,
      });
    }
  }, [isTeamLeavesError, teamLeavesError, loggedInEmployee?.name]);

  return (
    <Container fluid>
      {isLoading && <Loader />}

      <div className="d-block d-lg-none">
        <Row className="py-4">
          <Row className="mb-4">
            <Col>
              <H2 className="f-16">{title}</H2>
            </Col>
          </Row>

          <Row className="align-items-center jutify-content-between mb-3">
            <Col xs="12" className="mb-3">
              {statusFilterControl}
            </Col>

            <Col xs={canBulkApprove ? "6" : "12"}>
              <FinancialYearCycle
                selectedYear={selectedFinancialYear ?? ""}
                setSelectedYear={handleSetSelectedYear}
                startMonth={9}
                startDateValue={1}
                endMonth={8}
                endDateValue={30}
              />
            </Col>

            {canCreateAdjustment && (
              <Col xs="12" className="mt-2">
                <Btn color="primary" className="f-12" onClick={openAdjustmentModal}>Apply Leave</Btn>
              </Col>
            )}

            {canBulkApprove && (
              <Col xs="6">
                <Btn
                  color="primary"
                  className="f-12 w-100"
                  onClick={handleOpenTeamLeaveRequestModal}
                >
                  Approve/Reject All
                </Btn>
              </Col>
            )}
          </Row>

          {Array.isArray(teamLeavesData?.data) &&
            teamLeavesData?.data?.map((teamMember, index) => (
              <TeamMembersLeaveRequestCard
                key={index + 1}
                teamMember={teamMember}
                openModal={handleOpenModal}
              />
            ))}
        </Row>
      </div>

      <div className="d-none d-lg-block">
        <Row>
          <Col>
            <Card className="my-4 px-4 pb-4 pt-3">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
                <H2 className="f-16 m-0">{title}</H2>

                <div className="d-flex flex-wrap align-items-center justify-content-end gap-3">
                  {statusFilterControl}

                  <FinancialYearCycle
                    selectedYear={selectedFinancialYear ?? ""}
                    setSelectedYear={handleSetSelectedYear}
                    startMonth={9}
                    startDateValue={1}
                    endMonth={8}
                    endDateValue={30}
                  />

                  {canCreateAdjustment && (
                    <Btn color="primary" className="f-12" onClick={openAdjustmentModal}>Apply Leave</Btn>
                  )}
                </div>
              </div>

              <div className="table-responsive user-datatable custom-scrollbar mt-2 position-relative">
                <TeamMembersLeaveRequestTable
                  filterText={filterText}
                  setFilterText={setFilterText}
                  toggleCleared={toggleCleared}
                  onRowSelected={handleRowSelected}
                  openModal={handleOpenModal}
                  teamLeaveRequestList={teamLeavesData?.data ?? []}
                  teamLeaveRequestListLoading={isTeamLeaveRequestListLoading}
                />

                {canBulkApprove &&
                  selectRows &&
                  selectRows.selectedRows?.length > 0 && (
                    <BulkActionBar
                      label={`You have selected: ${selectRows.selectedRows.length}`}
                      selectedRows={selectRows.selectedRows}
                      onApprove={() =>
                        handleBulkLeaveRequestApproved("approved")
                      }
                      onReject={() =>
                        handleBulkLeaveRequestApproved("rejected")
                      }
                    />
                  )}
              </div>
            </Card>
          </Col>
        </Row>
      </div>

      {hasFullLeaveAccess &&
        modalType === approvalLeaveModal &&
        matchedLeave &&
        employeeLeaveTypeList?.data?.length &&
        selectedEmployeeInfo !== null && (
          <Suspense fallback={<Loader />}>
            <TeamMembersLeaveApprovedModal
              isOpen={isOpen}
              matchedLeave={matchedLeave}
              closeModal={handleCloseModal}
              employeeLeaveTypeList={employeeLeaveTypeList}
              employeeInfo={selectedEmployeeInfo}
              loggedUserType={userType}
            />
          </Suspense>
        )}

      {hasFullLeaveAccess &&
        modalType === bulkApprovedLeaveTeamRequestList && (
          <Suspense fallback={<Loader />}>
            <TeamMembersLeaveBulkApprovedModal
              isOpen={isOpen}
              closeModal={handleCloseModal}
              allTeamLeaveRequests={allTeamLeaveRequests}
              checkboxTeamLeaveList={checkboxTeamLeaveList}
              selectAllLeaveRequest={selectAllLeaveRequest}
              onCheckboxLeavesRequest={handleCheckboxLeavesRequest}
              onSelectAllLeaveRequest={handleSelectAllLeavesRequest}
              sortEmployeeLeavesRequestList={sortEmployeeLeavesRequestList}
              onSearchEmployeeLeaveRequest={handleSearchEmployeeLeaveRequest}
              handleBulkLeaveRequestApproved={handleBulkLeaveRequestApproved}
            />
          </Suspense>
        )}

      {canCreateAdjustment && modalType === leaveApplyModal && isOpen && (
        <Suspense fallback={<Loader />}>
          <EmployeeLeaveFormModal
            isOpen={isOpen}
            parentUserRole={undefined}
            employeeLeaveTypeList={adjustmentLeaveTypes ?? { success: true, data: [] }}
            hrAdjustment={{
              entryType: adjustmentType,
              users: usersResponse?.data ?? [],
              selectedUserId: adjustmentUserId,
              managerReady: adjustmentManagerReady,
              onUserChange: changeAdjustmentUser,
            }}
          />
        </Suspense>
      )}
    </Container>
  );
};

export default TeamMembersLeave;
